import { useCallback } from "react";
import { bool, number, string } from "prop-types";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useStores, useFeatureGuardian } from "@hooks";
import Grid from "@material-ui/core/Grid";
import { Button, ProjectActionMenu } from "@components";
import { VideoLabel } from "@material-ui/icons";
import { ArrowRight } from "@assets";
import { editor, pdfEditor, sessions } from "@paths";
import { LOCKED_FEATURES } from "@utils";

export const Footer = ({
  id,
  uuid,
  isMini,
  isContractor,
  isOwner,
  isBusiness,
  name,
  description,
  projectBtSetUp,
  projectBtExported,
  hasComments,
}) => {
  const { userStore } = useStores();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { checkAccess, FeatureAlert } = useFeatureGuardian(
    LOCKED_FEATURES.RECORDINGS
  );

  const { isDeveloper } = userStore;

  const canSeeRecordings = !isDeveloper && !isContractor;

  const openEditor = useCallback(() => {
    if (!uuid) return;
    navigate(editor(uuid));
  }, [uuid]);

  const openPdfEditor = useCallback(() => {
    if (!uuid) return;
    navigate(pdfEditor(uuid));
  }, [uuid]);

  const openSessionList = useCallback(async () => {
    if (!uuid) return;
    const allowed = await checkAccess();
    if (!allowed) return;
    navigate(sessions(uuid));
  }, [uuid, checkAccess]);

  return (
    <Grid item container justifyContent="space-between" spacing={2}>
      <Grid item container spacing={1} className="w-max">
        {canSeeRecordings && (
          <Grid item>
            <Button
              icon={<VideoLabel />}
              onClick={openSessionList}
              size="large"
              variant="contained"
              color="secondary"
            >
              {t("routes.recordings")}
            </Button>
            <FeatureAlert />
          </Grid>
        )}
        {!isContractor && (
          <Grid item>
            <ProjectActionMenu
              onlyDangerousActions
              useCreateTemplate
              fullButton
              id={id}
              uuid={uuid}
              isMini={isMini}
              isContractor={isContractor}
              isOwner={isOwner}
              isBusiness={isBusiness}
              name={name}
              description={description}
              projectBtSetUp={projectBtSetUp}
              projectBtExported={projectBtExported}
              hasComments={hasComments}
            />
          </Grid>
        )}
      </Grid>
      <Grid item>
        <Button
          color="primary"
          variant="contained"
          size="large"
          icon={<ArrowRight />}
          onClick={isMini ? openPdfEditor : openEditor}
        >
          {t("common.open")}
        </Button>
      </Grid>
    </Grid>
  );
};

Footer.propTypes = {
  id: number.isRequired,
  uuid: string.isRequired,
  isOwner: bool.isRequired,
  isContractor: bool,
  isMini: bool.isRequired,
  isBusiness: bool.isRequired,
  description: string,
  name: string,
  projectBtSetUp: bool,
  projectBtExported: bool,
  hasComments: bool,
};
